import { Component, EventEmitter, Inject, OnInit } from '@angular/core';
import { ModalData, ModalRef } from '@purespectrum1/ui/modal';
import {
  NestedQuota,
  QuotaPayload,
  SelectedNestedQuota,
} from '../../shared/interfaces/quota.interface';
import { Constants } from '../constants/quotas';
import { notifyMessage } from '../../shared/constants/notify-message';
import { QualificationForNest } from '../../shared/interfaces/qualification.interface';
import { QuotaNestingService } from './quota-nesting.service';
import { QUALIFICATION_CODES } from '../../shared/constants/qualification-code';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { NestedQuotaService } from '../services/nested-quota/nested-quota.service';
import { NestedAllocation } from './domain/nested-allocation';

@Component({
  selector: 'ps-ui-quota-nesting',
  templateUrl: './quota-nesting-modal.component.html',
  styleUrls: ['./quota-nesting-modal.component.css'],
})
export class QuotaNestingModalComponent implements OnInit {
  private readonly _qualifications: Array<QualificationForNest> =
    this._modalData.qualifications;
  private readonly _locationQualification: QualificationForNest =
    this._modalData.locationQualification;
  private readonly _quotas: Array<QuotaPayload> = this._modalData.quotas;
  private readonly _advanceTargetingQualifications: Array<QualificationForNest> =
    this._modalData.advanceTargetingQualifications;
  private readonly _surveyGoal: number = this._modalData.completes;
  private readonly _syncNestedQuotasEmmiter: EventEmitter<Array<NestedQuota>> =
    this._modalData.syncNestedQuotasEmmiter;
  public allQualifications: Array<QualificationForNest> = [];
  public nestedQuotas: Array<NestedQuota> = this._modalData.nestedQuotas;
  private _startedWithNestedQuotas: boolean = false;
  public disableApplyNesting: boolean = true;
  public hasMoreQuotasThanCompletes: boolean = false;
  public selectedNestedQuotas: Array<SelectedNestedQuota> = [];
  private _orderNestedQuotas: OrderNestedQuota[] = [];
  public initialSelectedQualification: Array<QualificationForNest> = [];
  public initalNestedQuotas: Array<NestedQuota> = [];

  public get isEditMode() {
    return this._modalData.isEditMode;
  }

  constructor(
    @Inject(ModalData)
    private _modalData: any,
    private _modalRef: ModalRef<
      QuotaNestingModalComponent,
      {
        quotas: QuotaPayload[];
      }
    >,
    private readonly _quotaNestingService: QuotaNestingService,
    private _toastr: ToasterService,
    private nestedQuotaService: NestedQuotaService
  ) {}

  public ngOnInit(): void {
    if (!this.initalNestedQuotas.length) {
      this.initalNestedQuotas = [...this.nestedQuotas];
    }
    this._getQualifications();
    this._setUpNestedFields();
    this._setUpVisibleQuotas();
    this._setUpFlags();
    this._checkIfHasMoreQuotasThanCompletes();
  }

  private _getQualifications() {
    this.allQualifications =
      this._quotaNestingService.getAllQualificationsForNest(
        this._qualifications,
        this._advanceTargetingQualifications
      );
  }

  private _setUpNestedFields(): void {
    if (this.nestedQuotas && this.nestedQuotas.length) {
      this.selectedNestedQuotas = this._getCriteriaForNested(
        this.nestedQuotas[0].criteria
      );
      this.selectedNestedQuotas.forEach((q) => {
        this.allQualifications
          .filter((ql) => q.qualification_code === ql.qualification_code)
          .forEach((ql) => {
            ql.isSelected = true;
          });
      });
      this._initNestedQuotasSorting();
      this._nestSelectedQualifications();
    }
  }

  private _initNestedQuotasSorting() {
    this._orderNestedQuotas = this.nestedQuotas[0].criteria.map(
      (criteria, index): OrderNestedQuota => ({
        order: index,
        qualificationCode: criteria.qualification_code,
        qualificationName: criteria.qualification_name,
      })
    );
  }

  private _getCriteriaForNested(nestedCriteria: any) {
    return nestedCriteria.filter(
      (criteria: any) =>
        ![
          QUALIFICATION_CODES.CHILD_GENDER,
          QUALIFICATION_CODES.CHILD_AGE,
        ].includes(criteria.qualification_code)
    );
  }

  private _setUpVisibleQuotas(): void {
    for (const quota of this._quotas) {
      const quotaQualificationCodes = this._getQuotaQualificationCode(quota);
      for (const qualification of this.allQualifications) {
        if (
          quotaQualificationCodes.includes(qualification.qualification_code) &&
          !qualification.isAdvanceTargetingParent
        ) {
          qualification.isVisible = true;
          qualification.isSelected = !!qualification.hasNestedQuotas;
        }
      }
    }
  }

  private _setUpFlags(): void {
    this._startedWithNestedQuotas = this.allQualifications.some(
      (qualification) => qualification.isSelected
    );
  }

  public clearNesting(): void {
    this.nestedQuotas = [];
    this.selectedNestedQuotas = [];

    for (const qualificationForNest of this.allQualifications) {
      qualificationForNest.isSelected = false;
    }

    this._sendNestedQuotasToParent();
  }

  public applyNesting(): void {
    this._sendNestedQuotasToParent();
    this._modalRef.close();
  }

  public toggleSelectQualification(
    qualificationForNest: QualificationForNest
  ): void {
    const activate: boolean = !qualificationForNest.isSelected;
    if (activate) {
      this._hasHitTheNestedQuotaLimits();
      qualificationForNest.isSelected = activate;
      this.disableApplyNesting = false;
    } else {
      qualificationForNest.isSelected = activate;
      qualificationForNest.hasNestedQuotas = false;
      this.disableApplyNesting = !!(
        !this._startedWithNestedQuotas &&
        !this.allQualifications.some(
          (qualification) => qualification.isSelected
        )
      );
    }
    this._setNestedQuotasSortingOnSelection(qualificationForNest);
    this._nestSelectedQualifications();
  }

  private _setNestedQuotasSortingOnSelection(
    qualificationForNest: QualificationForNest
  ) {
    const { isSelected } = qualificationForNest;
    if (isSelected) {
      this._orderNestedQuotas.push({
        order: this.selectedNestedQuotas.length,
        qualificationCode: qualificationForNest.qualification_code,
        qualificationName: qualificationForNest.qualification_name,
      });
    } else {
      const idx = this._orderNestedQuotas.findIndex(
        (quotaOrder) =>
          quotaOrder.qualificationCode ===
          qualificationForNest.qualification_code
      );
      if (idx > -1) {
        let { order } = this._orderNestedQuotas[idx];
        this._orderNestedQuotas.splice(idx, 1);
        this._orderNestedQuotas
          .filter(({ order: quotaOrder }) => quotaOrder > order)
          .forEach((orderAfter) => {
            orderAfter.order -= 1;
          });
      }
    }
  }

  private _nestSelectedQualifications(): void {
    const selectedQualificationsToNest = this.allQualifications.filter(
      (qualification) => qualification.isSelected
    );

    if (!this.initialSelectedQualification.length) {
      this.initialSelectedQualification = [...selectedQualificationsToNest];
    }

    this.selectedNestedQuotas = [...selectedQualificationsToNest];
    if (!selectedQualificationsToNest.length) {
      this.nestedQuotas = [];
      return;
    }
    this._sortSelectedNestedQuotas();
    this.nestedQuotas =
      this._quotaNestingService.nestQuotasForQualifications(
        this._quotas,
        selectedQualificationsToNest,
        this._surveyGoal,
        this._orderNestedQuotas
      ) ?? [];

    if (
      this.initalNestedQuotas.length === this.nestedQuotas.length &&
      this.initialSelectedQualification.length ===
        selectedQualificationsToNest.length
    ) {
      this.nestedQuotas.forEach((quota, index) => {
        const initialQuota = this.initalNestedQuotas[index];
        Object.assign(quota, {
          required_count: initialQuota.required_count,
          percentage: initialQuota.percentage,
          minimum: initialQuota.minimum,
          maximum: initialQuota.maximum,
          number: initialQuota.number,
          current_target: initialQuota.current_target,
        });
      });
    }

    this._checkIfHasMoreQuotasThanCompletes();
    this.nestedQuotas = new NestedAllocation(
      this._surveyGoal,
      this.nestedQuotas
    ).audit();
  }

  private _sortSelectedNestedQuotas() {
    const findQuotaOrder = (quota: SelectedNestedQuota): number =>
      this._orderNestedQuotas.find(
        (quotaOrder) =>
          quotaOrder.qualificationCode === quota.qualification_code
      )?.order || 0;
    this.selectedNestedQuotas.sort(
      (a, b) => findQuotaOrder(a) - findQuotaOrder(b)
    );
  }

  private _hasHitTheNestedQuotaLimits(): void {
    const activeQuotas = this.allQualifications.filter(
      (qualification) => qualification.isSelected
    );
    if (activeQuotas.length >= Constants.NESTED_QUOTA_LIMIT) {
      throw this._toastr.warning(
        notifyMessage.warningMessage.QUOTA.MAXIMUM_QUOTAS_TO_NEST.replace(
          ':NESTED_QUOTA_LIMIT',
          `${Constants.NESTED_QUOTA_LIMIT}`
        )
      );
    }
  }

  private _getQuotaQualificationCode(quota: QuotaPayload): Array<number> {
    const qualificationsInQuotaCriteria: number[] = [];
    quota.criteria.forEach((criteria) => {
      qualificationsInQuotaCriteria.push(criteria.qualification_code);
    });
    return qualificationsInQuotaCriteria;
  }

  private _sendNestedQuotasToParent(): void {
    this._syncNestedQuotasEmmiter.emit(this.nestedQuotas);
  }

  private _checkIfHasMoreQuotasThanCompletes(): void {
    this.hasMoreQuotasThanCompletes =
      this.nestedQuotas.length > this._surveyGoal;
  }

  public updateQuotaPercentage(nestedQuota: NestedQuota) {
    this.nestedQuotaService.updateQuotaPercentage(
      nestedQuota,
      this.nestedQuotas,
      this._surveyGoal
    );
  }

  public updateQuotaAllocation(nestedQuota: NestedQuota) {
    this.nestedQuotaService.updateQuotaAllocation(
      nestedQuota,
      this.nestedQuotas,
      this._surveyGoal
    );
  }

  public setInitialAllocation(nestedQuota: NestedQuota) {
    this.nestedQuotaService.setInitialAllocation(nestedQuota);
  }
}

interface OrderNestedQuota {
  order: number;
  qualificationCode: number;
  qualificationName?: string;
}
